import React, { useEffect, useState, useRef } from 'react';
import { BsFolder2Open } from 'react-icons/bs';
import {
  IoLogOutOutline,
  IoClose,
  IoMenu,
  IoPencil,
  IoCheckmark,
  IoTrashOutline,
  IoDocumentTextOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { pages } from '@microsoft/teams-js';

import { API_URL } from '../services/api';
import { DataState } from '../context/DataProvider';
import { useTeams } from '../context/TeamsProvider';
import ProfileSettingsModal from './ProfileSettingsModal';

const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID;

const Sidebar = ({ toggleSidebar, isOpen, onToggle }) => {
  const { isTeamsInitialized } = useTeams();
  const {
    tasks,
    setTasks,
    currentTaskId,
    setCurrentTaskId,
    setTools,
    loadConversations,
    handleCreateTask,
    setJustChangedLocalStorageData,
    setSelectedReason,
    setIsReasoning,
    setIsWebSearch,
    setIsCanvas,
    setIsAgentMode,
    setAgentSequence,
    userInfo,
    setTaskMCPConnections,
    setTaskOqlousTools,
    setTaskManualToolSelection,
    setCurrentTaskModel,
    handleDeleteTask,
    detachActiveTaskStreams,
  } = DataState();

  const [openSettings, setOpenSettings] = useState(false);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();

  // --- STATE FOR EDITING ---
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);

  // --- STATE FOR DELETE CONFIRMATION ---
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingTaskId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingTaskId]);

  const handleLogout = () => {
    async function logout() {
      try {
        await fetch(`${API_URL}/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (error) {
        toast.error('Logout failed. Please try again.');
      } finally {
        localStorage.clear();
        setJustChangedLocalStorageData(true);
        navigate('/login');
      }
    }
    logout();
  };

  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem('projectToken');
    if (!storedUserGoogleId) {
      navigate('/login');
    }
  }, [navigate]);

  const handleProjectsButtonClick = () => {
    if (!isTeamsInitialized) {
      navigate('/projects');
      return;
    }
    const targetPageId = 'projects';
    if (pages.currentApp.isSupported()) {
      pages.currentApp
        .navigateTo({ pageId: targetPageId })
        .catch(
          toast.error(
            'Failed to navigate to Projects page in Teams. Please try again.',
          ),
        );
    } else {
      pages
        .navigateToApp({ appId: TEAMS_APP_ID, pageId: targetPageId })
        .catch(
          toast.error(
            'Failed to open Projects page in Teams. Please try again.',
          ),
        );
    }
  };

  const handleTaskSelect = (task) => {
    // Immediately detach any active SSE streams from the previous chat
    // BEFORE triggering loadConversations. This ensures the old stream
    // doesn't block network resources or overwrite the new chat's state.
    detachActiveTaskStreams(task._id);

    setCurrentTaskId(task._id);
    setSelectedReason(task.reasoning_effort || null);
    setIsWebSearch(task.is_web_search || false);
    setIsReasoning(task.is_reasoning || false);
    setIsCanvas(task.is_canvas || false);
    setIsAgentMode(task.is_agent_mode || false);
    setAgentSequence(task.agent_sequence || ['Orchestrator']);
    setTaskMCPConnections(task.mcp_connections || []);
    setTaskOqlousTools(task.oqlous_tools || []);
    setTaskManualToolSelection(task.manual_tool_selection || false);
    setCurrentTaskModel(task.task_model || null);
    loadConversations(task._id);
    setTools(tasks.find((task1) => task1._id === task._id)?.tools || []);

    if (window.innerWidth < 768 && onToggle) {
      onToggle();
    }
  };

  // --- NEW: HANDLE RENAME ---
  const startEditing = (e, task) => {
    e.stopPropagation(); // Prevent selecting the task
    setEditingTaskId(task._id);
    setEditValue(task.task_description);
  };

  const cancelEditing = (e) => {
    if (e) e.stopPropagation();
    setEditingTaskId(null);
    setEditValue('');
  };

  const saveTaskName = async (e, taskId) => {
    if (e) e.stopPropagation();

    if (
      !editValue.trim() ||
      editValue === tasks.find((t) => t._id === taskId)?.task_description
    ) {
      cancelEditing();
      return;
    }

    const token = localStorage.getItem('projectToken');

    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ task_description: editValue }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update local state immediately to reflect change
        if (setTasks) {
          setTasks((prevTasks) => {
            const taskIndex = prevTasks.findIndex((t) => t._id === taskId);
            if (taskIndex === -1) return prevTasks;

            const updatedTask = {
              ...prevTasks[taskIndex],
              task_description: editValue,
            };

            const otherTasks = prevTasks.filter((_, idx) => idx !== taskIndex);
            return [updatedTask, ...otherTasks];
          });
        }
        toast.success('Agent renamed');
      } else {
        toast.error(data.error || 'Failed to rename');
      }
    } catch (error) {
      toast.error('Network error occurred');
    } finally {
      setEditingTaskId(null);
    }
  };

  const handleKeyDown = (e, taskId) => {
    if (e.key === 'Enter') {
      saveTaskName(e, taskId);
    } else if (e.key === 'Escape') {
      cancelEditing(e);
    }
  };

  // --- DELETE HANDLERS ---
  const confirmDelete = async (e, taskId) => {
    e.stopPropagation();
    await handleDeleteTask(taskId);
    setDeletingTaskId(null);
    toast.success('Chat deleted');
  };

  const cancelDelete = (e) => {
    if (e) e.stopPropagation();
    setDeletingTaskId(null);
  };

  // Close sidebar logic...
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && window.innerWidth < 768) {
        const sidebar = sidebarRef.current;
        const menuButton = document.querySelector('[aria-label="Toggle menu"]');
        if (
          sidebar &&
          !sidebar.contains(event.target) &&
          menuButton &&
          !menuButton.contains(event.target) &&
          !event.target.closest('.modal, [role="dialog"]') &&
          !openSettings
        ) {
          onToggle && onToggle();
        }
      }
    };

    if (isOpen) {
      // Add delay to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onToggle, openSettings]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sidebarContent = (
    <div className="bg-1 flex h-full flex-col justify-between gap-1 overflow-hidden rounded-none md:rounded-2xl">
      {/* Header Section */}
      <div className="relative flex flex-col items-center gap-3 px-3 py-4 md:px-2">
        {/* Mobile Header with Close Button */}
        <div className="mb-2 flex w-full items-center justify-between md:hidden">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggle && onToggle();
            }}
            className="absolute right-1 top-1 z-50 rounded-lg p-2 transition-colors hover:bg-white hover:bg-opacity-10"
          >
            <IoClose className="text-xl" />
          </button>
        </div>

        {/* Logo Container */}
        <div className="mb-1 flex w-full items-center justify-center gap-3 md:mb-4">
          {/* Main Logo (Always visible) */}
          <img
            src={`../assets/logo.png`}
            alt="Main Logo"
            className={`${toggleSidebar ? 'h-8 md:h-10' : 'h-10 md:h-12'} transform object-contain transition-all duration-100`}
          />

          {/* Partner Logo (Conditionally renders if it exists) */}
          {userInfo?.partnerLogo && !toggleSidebar && (
            <>
              {/* Visual Separator for clarity - RESTORED COMMENT */}
              <div
                className="h-6 border-l border-white/30"
                aria-hidden="true"
              ></div>

              <img
                src={`../assets/partners/${userInfo.partnerLogo}`}
                alt="Partner Logo"
                className="h-7 object-contain md:h-9"
              />
            </>
          )}
        </div>
        {/* <img
          src={theme === "light" ? "../assets/logo.png" : "../assets/logo.png"}
          alt="Logo"
          className="w-full h-8 mb-2 text-center md:mb-4 md:h-10"
        /> */}

        {/* Action Buttons */}
        <div className="flex w-full flex-col gap-3 md:gap-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleCreateTask();
              if (window.innerWidth < 768 && onToggle) {
                setTimeout(() => onToggle(), 100);
              }
            }}
            className={`flex cursor-pointer items-center justify-center 
              gap-2 rounded-xl bg-primary-gradient px-3 py-3 text-sm 
              text-white transition-all duration-200 hover:opacity-90 sm:text-sm md:py-2 md:text-xs 
              ${toggleSidebar && 'text-xl font-bold'}`}
          >
            {toggleSidebar ? '+' : 'New Chat'}
          </button>

          {!toggleSidebar && (
            <button
              className="flex items-center justify-between gap-1 px-2 text-sm font-semibold transition-colors hover:text-opacity-80 md:text-xs"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              Recent Agents
            </button>
          )}
        </div>
      </div>

      {/* History Section */}
      {!toggleSidebar && (
        <div className="hide-sidebar bg-2 mx-2 -mt-3 flex flex-1 flex-col gap-1 overflow-y-auto rounded-xl p-3 md:mx-2">
          {/* Changed gap-2 to gap-1 above for tighter list */}
          {tasks?.length > 0 ? (
            tasks.map((task) => (
              // eslint-disable-next-line
              <div
                key={task._id}
                role="button"
                tabIndex={0}
                className="group relative"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (editingTaskId === task._id) return;
                  handleTaskSelect(task);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (editingTaskId === task._id) return;
                    handleTaskSelect(task);
                  }
                }}
              >
                {/* eslint-disable-next-line */}
                <div
                  className={`${
                    task._id === currentTaskId
                      ? 'bg-white bg-opacity-15'
                      : 'bg-white bg-opacity-5'
                  } flex min-h-[30px] cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-1 transition-all duration-200
                    hover:bg-zinc-800 md:py-1`}
                >
                  {/* --- EDIT MODE --- */}
                  {editingTaskId === task._id ? (
                    <div className="flex w-full items-center gap-2">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, task._id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full border-b border-[#5652CF] bg-transparent text-sm text-white outline-none focus:border-blue-400 sm:text-sm md:text-xs"
                      />
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => saveTaskName(e, task._id)}
                          className="p-1 text-green-400 hover:text-green-300"
                        >
                          <IoCheckmark />
                        </button>
                        <button
                          onClick={(e) => cancelEditing(e)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <IoClose />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* --- VIEW MODE --- */
                    <>
                      <p className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm sm:text-sm md:text-xs">
                        {task.task_description}
                      </p>

                      {/* Edit Icon - Hidden by default, visible on group-hover */}
                      {!editingTaskId && (
                        <div className="absolute right-0 flex items-center gap-1 bg-zinc-800 px-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={(e) => startEditing(e, task)}
                            className="p-1 text-gray-400 hover:text-amber-400"
                            title="Rename"
                          >
                            <IoPencil className="text-sm" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingTaskId(task._id);
                            }}
                            className="p-1 text-gray-400 hover:text-red-400"
                            title="Delete"
                          >
                            <IoTrashOutline className="text-sm" />
                          </button>
                        </div>
                      )}

                      {/* Delete confirmation */}
                      {deletingTaskId === task._id && (
                        <div className="absolute inset-0 z-10 flex items-center justify-between rounded-lg bg-red-900 bg-opacity-90 px-3">
                          <span className="text-xs text-white">Delete?</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => confirmDelete(e, task._id)}
                              className="p-1 text-green-400 hover:text-green-300"
                            >
                              <IoCheckmark />
                            </button>
                            <button
                              onClick={(e) => cancelDelete(e)}
                              className="p-1 text-red-400 hover:text-red-300"
                            >
                              <IoClose />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col gap-3 p-2 text-center text-sm">
              No recent chats
            </div>
          )}
        </div>
      )}

      {/* Bottom Actions */}
      <div className="flex w-full flex-col gap-1 border-t border-white border-opacity-10 px-3 py-3 text-sm md:border-none md:px-2">
        {/* +++ Make the User Info section a button to open the modal +++ - RESTORED COMMENT */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenSettings(true);
            // Close sidebar on mobile
            if (window.innerWidth < 768 && onToggle) {
              setTimeout(() => onToggle(), 100);
            }
          }}
          className={`flex w-full gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          {userInfo && (
            <div className="flex items-center gap-3">
              {userInfo.picture ? (
                <img
                  src={userInfo.picture}
                  alt={userInfo.name}
                  className="h-[25px] w-[25px] rounded-full object-cover"
                />
              ) : (
                <div className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-gray-300 text-sm font-bold text-gray-600">
                  {userInfo.name
                    ? userInfo.name?.charAt(0).toUpperCase()
                    : userInfo.email?.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className={`max-w-20 overflow-hidden text-ellipsis whitespace-nowrap text-sm md:max-w-24 ${
                  toggleSidebar && 'hidden'
                }`}
              >
                {userInfo.name || userInfo.email}
              </span>
            </div>
          )}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate('/report-templates');
            if (window.innerWidth < 768 && onToggle) {
              setTimeout(() => onToggle(), 100);
            }
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <IoDocumentTextOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Report Templates</span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleProjectsButtonClick();
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <BsFolder2Open className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Projects</span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleLogout();
          }}
          className={`flex gap-3 rounded-lg ${
            toggleSidebar ? 'justify-center' : 'justify-start'
          } items-center p-3 transition-all duration-200 hover:bg-white hover:bg-opacity-10 md:p-2`}
        >
          <IoLogOutOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Log out</span>
          )}
        </button>
      </div>

      {/* Settings Modal */}
      <ProfileSettingsModal
        isOpen={openSettings}
        onClose={() => setOpenSettings(false)}
        userInfo={userInfo}
      />
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden h-full md:block">{sidebarContent}</div>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop */}
          {/* eslint-disable-next-line */}
          <div
            role="presentation"
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />

          {/* Sidebar - Increased width and added ref */}
          {/* eslint-disable-next-line */}
          <div
            ref={sidebarRef}
            id="mobile-sidebar"
            role="presentation"
            className={`relative h-full w-80 max-w-[70vw] transform bg-gray-900 transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Mobile Menu Button - Only show when sidebar is closed */}
      {!isOpen && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggle();
          }}
          className="fixed left-4 top-5 z-40 rounded-lg bg-opacity-50 p-2 backdrop-blur-sm transition-all duration-200 hover:bg-opacity-70 md:hidden"
          aria-label="Toggle menu"
        >
          <IoMenu className="text-2xl text-white" />
        </button>
      )}
    </>
  );
};

export default Sidebar;
